import {
  AUTH_COOKIE_SAME_SITE,
  AUTH_PROVIDER_COOKIE,
  authProviderCookieDeleteIdentity,
  serverAuthProviderCookieIdentity,
} from '@/utils/auth/authProviderCookie';
import { deleteCookie } from 'cookies-next/client';

/**
 * Auth0 token claim that signals the user's session must be non-persistent
 * (browser-close = logout). When present, `onCallback` sets the two
 * cookies below; middleware uses them to detect browser-close.
 */
export const FORCED_COOKIE_MODE_CLAIM = 'forced_cookie_mode';

/**
 * Persistent (30-day maxAge): remembers that non-persistent sessions are
 * required for this user even after the alive cookie expires.
 */
export const NON_PERSISTENT_ENFORCED_COOKIE = 'heidi-nonpersistent-enforced';

/**
 * Session-only (no maxAge): browser deletes on close. Middleware uses
 * presence of ENFORCED + absence of ALIVE as the browser-close signal.
 */
export const NON_PERSISTENT_ALIVE_COOKIE = 'heidi-nonpersistent-alive';

/**
 * Full attributes for the two non-persistent session cookies.
 * `httpOnly: true` — middleware-only read; never accessed by client JS.
 * Reuses `serverAuthProviderCookieIdentity` for path + domain so server-set
 * auth cookies share one identity source.
 */
export const nonPersistentSessionCookieAttrs = {
  ...serverAuthProviderCookieIdentity,
  httpOnly: true,
  // SameSite=None (iframe embedding) requires Secure. Chrome accepts Secure on
  // http://localhost, so local dev still works without HTTPS.
  secure: true,
  sameSite: AUTH_COOKIE_SAME_SITE,
} as const;

/**
 * Clears the client-readable Auth0 discriminator cookie from the browser.
 *
 * Only `AUTH_PROVIDER_COOKIE` is deleted here — it is the sole auth cookie
 * that is not `HttpOnly`, so it's the only one reachable from
 * `document.cookie`. Deleted with the full delete identity — path +
 * public domain PLUS `sameSite: 'none'` + `secure: true`. Cookies-next
 * requires the delete tuple to match the set tuple exactly; the set
 * carries `SameSite=None; Secure` (`authProviderCookieAttrs`), and
 * omitting either on the delete causes Chrome to reject the
 * expired-cookie write in third-party iframe contexts (EHR embeds).
 * Uses `authProviderCookieDeleteIdentity` so the delete tuple is
 * derived from the same constants as the set attrs and can't drift
 * again.
 *
 * The two non-persistent session markers (`NON_PERSISTENT_ENFORCED_COOKIE`,
 * `NON_PERSISTENT_ALIVE_COOKIE`) are `HttpOnly`; client-side JS cannot remove
 * them and attempting to is a silent no-op. They are expired server-side by
 * `clearServerSideAuth0Cookies` in `src/middleware.ts` when the logout flow
 * redirects through `/api/auth0/logout` (the Auth0 logout path always does).
 */
export function clearAuth0Cookies(): void {
  deleteCookie(AUTH_PROVIDER_COOKIE, authProviderCookieDeleteIdentity);
}
